import { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, NativeSelect, Stack, Switch, Text,
} from '@chakra-ui/react'
import { toaster } from './ui/toaster'
import { useTranslation } from '../i18n'
import { useAuth } from '../contexts/AuthContext'
import { VideoAIBackfillDialog } from './VideoAIBackfillDialog'
import { VideoAIBackfillCard } from './VideoAIBackfillProgress'
import { useVideoAIBackfill } from '../hooks/useVideoAIBackfill'
import {
  VIDEO_AI_FEATURES, DEFAULT_VIDEO_AI_CONFIG, EXPLICIT_THRESHOLDS,
  FREE_TIER_MINUTES, enabledFeatureKeys, estimateCostPerSecond, pricePerMinute,
} from '../constants/videoAI'
import { fetchVideoAIConfig, saveVideoAIConfig } from '../services/videoAI'

const usd = (value, digits = 2) =>
  `$${value.toFixed(digits)}`

export function EventVideoAI({ eventId }) {
  const { t } = useTranslation()
  const { isSysAdmin } = useAuth()
  const [config, setConfig] = useState(DEFAULT_VIDEO_AI_CONFIG)
  // The last config the server actually has. The backfill dialog prices the
  // persisted config, not whatever is half-edited on screen, so it needs its
  // own copy to compare against rather than trusting `config` directly.
  const [savedConfig, setSavedConfig] = useState(DEFAULT_VIDEO_AI_CONFIG)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // The event's latest backfill job. Read only by sysadmins, the only ones
  // who can start one.
  const backfill = useVideoAIBackfill(eventId, isSysAdmin)
  const backfillRunning = backfill?.status === 'running'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchVideoAIConfig(eventId)
      .then((next) => {
        if (cancelled) return
        setConfig(next)
        setSavedConfig(next)
      })
      .catch((err) => console.warn('Could not read AI settings:', err.message))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [eventId])

  const setFeature = useCallback((key, checked) => {
    setConfig((prev) => ({ ...prev, features: { ...prev.features, [key]: checked } }))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await saveVideoAIConfig(eventId, config)
      setSavedConfig(config)
      toaster.create({ title: t('videoAI.saved'), type: 'success' })
    } catch (err) {
      toaster.create({ title: t('videoAI.saveFailed'), description: err.message, type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // The backfill dialog quotes the persisted config's price. If the screen
  // has edits that were never saved, that quote would be honest about a
  // config the server is not actually going to run — so the button stays
  // disabled until the edits are saved or discarded.
  const hasUnsavedChanges = JSON.stringify(config) !== JSON.stringify(savedConfig)

  const keys = enabledFeatureKeys(config)
  const perMinute = pricePerMinute(keys)
  const perSecond = estimateCostPerSecond(keys)

  return (
    <>
      <Stack gap={3} opacity={loading ? 0.5 : 1}>
        <Switch.Root
          checked={config.enabled === true}
          onCheckedChange={(d) => setConfig((p) => ({ ...p, enabled: d.checked }))}
        >
          <Switch.HiddenInput /><Switch.Control />
          <Switch.Label>{t('videoAI.enable')}</Switch.Label>
        </Switch.Root>

        {config.enabled && (
          <>
            <Text fontWeight="600" fontSize="sm">{t('videoAI.featuresLabel')}</Text>
            <Stack gap={1} pl={2}>
              {VIDEO_AI_FEATURES.map((f) => (
                <Switch.Root
                  key={f.key}
                  size="sm"
                  checked={config.features[f.key] === true}
                  onCheckedChange={(d) => setFeature(f.key, d.checked)}
                >
                  <Switch.HiddenInput /><Switch.Control />
                  <Switch.Label>{t(f.labelKey)}</Switch.Label>
                </Switch.Root>
              ))}
            </Stack>

            {config.features.explicitContent && (
              <Box pl={2}>
                <Switch.Root
                  checked={config.aiModeration === true}
                  onCheckedChange={(d) => setConfig((p) => ({ ...p, aiModeration: d.checked }))}
                  colorPalette="red"
                >
                  <Switch.HiddenInput /><Switch.Control />
                  <Switch.Label>{t('videoAI.aiModeration')}</Switch.Label>
                </Switch.Root>
                <Text fontSize="sm" mt={2}>{t('videoAI.thresholdLabel')}</Text>
                <NativeSelect.Root size="sm" width="220px" mt={1}>
                  <NativeSelect.Field
                    value={config.explicitThreshold}
                    onChange={(e) => setConfig((p) => ({ ...p, explicitThreshold: e.target.value }))}
                  >
                    {EXPLICIT_THRESHOLDS.map((level) => (
                      <option key={level} value={level}>{t(`videoAI.threshold${level}`)}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Box>
            )}

            <Box fontSize="sm" color="fg.muted">
              {keys.length === 0
                ? t('videoAI.costNone')
                : (
                  <>
                    <Text>{t('videoAI.costLine', {
                      perSecond: usd(perSecond, 6),
                      perMinute: usd(perMinute, 3),
                    })}</Text>
                    <Text>{t('videoAI.costFree', { minutes: FREE_TIER_MINUTES })}</Text>
                  </>
                )}
            </Box>
          </>
        )}

        <Stack direction="row" gap={3} align="center">
          <Button size="sm" colorPalette="brand" loading={saving} onClick={handleSave}>
            {t('common.save')}
          </Button>
          {isSysAdmin && config.enabled && keys.length > 0 && (
            <VideoAIBackfillDialog
              eventId={eventId}
              disabled={hasUnsavedChanges || backfillRunning}
            />
          )}
        </Stack>
        {isSysAdmin && config.enabled && keys.length > 0 && hasUnsavedChanges && (
          <Text fontSize="sm" color="yellow.400">{t('videoAI.backfillUnsavedChanges')}</Text>
        )}
        {/* Outside the enabled-and-has-features gate on purpose: a job keeps
            running, and can still be stopped, after analysis is switched off. */}
        {isSysAdmin && <VideoAIBackfillCard eventId={eventId} job={backfill} />}
      </Stack>
    </>
  )
}
